import type { ReactElement, ReactNode } from 'react';
import { Badge, Card, Group, NumberInput, SegmentedControl, Select, Stack, TagsInput, Text, Textarea } from '@mantine/core';
import { useBreakpointUp } from '@/shared/responsive/useBreakpoint';
import { DollarSign, Gauge, List, Percent, Pilcrow } from 'lucide-react';
import type { ClassificationOutputType } from './ClassificationService';
import { CURRENCY_OPTIONS, OUTPUT_TYPES, describeOutputConfig, type CategoryNoun } from './ClassificationOptions';

interface ClassificationInstructionsCardProps {
    instructions: string;
    outputType: ClassificationOutputType;
    outputConfig: Record<string, unknown>;
    noun: CategoryNoun;
    onInstructionsChange: (value: string) => void;
    onOutputTypeChange: (value: ClassificationOutputType) => void;
    onOutputConfigChange: (patch: Record<string, unknown>) => void;
}

const TOKEN_ESTIMATE_DIVISOR = 4;

const OUTPUT_TYPE_ICONS: Record<ClassificationOutputType, ReactNode> = {
    scale: <Gauge size={14} />,
    currency: <DollarSign size={14} />,
    percent: <Percent size={14} />,
    text: <Pilcrow size={14} />,
    category: <List size={14} />,
};

export default function ClassificationInstructionsCard({
    instructions,
    outputType,
    outputConfig,
    noun,
    onInstructionsChange,
    onOutputTypeChange,
    onOutputConfigChange,
}: ClassificationInstructionsCardProps): ReactElement {
    const isWide = useBreakpointUp('sm');
    const meta = OUTPUT_TYPES.find((t) => t.value === outputType) ?? OUTPUT_TYPES[0]!;
    const badgeDetail = describeOutputConfig(outputType, outputConfig);
    const badgeLabel = badgeDetail ? `${meta.badge} · ${badgeDetail}` : meta.badge;
    const approxTokens = Math.max(1, Math.round(instructions.length / TOKEN_ESTIMATE_DIVISOR));

    return (
        <Card withBorder radius="md" padding="lg">
            <Stack gap="xs" mb="md">
                <Text fw={800} size="sm">
                    2 · {noun.singular} Instructions
                </Text>
                <Text size="xs" c="dimmed">
                    The instruction the model runs against each user session. Use {'{{double_braces}}'} for per-session variables.
                </Text>
            </Stack>

            <Stack gap="lg">
                <Stack gap={6}>
                    <Group justify="space-between">
                        <Text size="xs" fw={600} c="dimmed" tt="uppercase">
                            Instructions
                        </Text>
                        <Text size="xs" c="dimmed">
                            ~{approxTokens} tokens
                        </Text>
                    </Group>
                    <Textarea
                        placeholder={'You are a usage-classification engine…\nFor the session below, decide whether it matches…\nContext: {{session.transcript}}'}
                        autosize
                        minRows={6}
                        value={instructions}
                        onChange={(e) => onInstructionsChange(e.currentTarget.value)}
                        styles={{ input: { fontFamily: 'var(--mantine-font-family-monospace)', fontSize: 12.5 } }}
                    />
                </Stack>

                <Stack gap="sm">
                    <Text size="xs" fw={600} c="dimmed" tt="uppercase">
                        Output type
                    </Text>
                    {isWide ?
                        <SegmentedControl
                            fullWidth
                            radius="md"
                            value={outputType}
                            onChange={(v) => onOutputTypeChange(v as ClassificationOutputType)}
                            data={OUTPUT_TYPES.map((t) => ({
                                value: t.value,
                                label: (
                                    <Group gap={6} justify="center" wrap="nowrap">
                                        {OUTPUT_TYPE_ICONS[t.value]}
                                        <span>{t.label}</span>
                                    </Group>
                                ),
                            }))}
                        />
                    :   <Select
                            radius="md"
                            aria-label="Output type"
                            allowDeselect={false}
                            value={outputType}
                            onChange={(v) => v && onOutputTypeChange(v as ClassificationOutputType)}
                            data={OUTPUT_TYPES.map((t) => ({ value: t.value, label: t.label }))}
                        />
                    }

                    <Card withBorder radius="sm" padding="md" bg="var(--mantine-primary-color-light)">
                        <Group align="center" gap="sm" wrap="nowrap">
                            <Badge variant="white" radius="sm" style={{ flexShrink: 0 }} styles={{ label: { overflow: 'visible' } }}>
                                {badgeLabel}
                            </Badge>
                            <Text size="xs" c="dimmed" style={{ lineHeight: 1.5 }}>
                                {meta.description}
                            </Text>
                        </Group>
                        <OutputConfigEditor outputType={outputType} outputConfig={outputConfig} onChange={onOutputConfigChange} />
                    </Card>
                </Stack>
            </Stack>
        </Card>
    );
}

interface OutputConfigEditorProps {
    outputType: ClassificationOutputType;
    outputConfig: Record<string, unknown>;
    onChange: (patch: Record<string, unknown>) => void;
}

function OutputConfigEditor({ outputType, outputConfig, onChange }: OutputConfigEditorProps): ReactElement | null {
    if (outputType === 'scale') {
        const min = typeof outputConfig.min === 'number' ? outputConfig.min : undefined;
        const max = typeof outputConfig.max === 'number' ? outputConfig.max : undefined;
        return (
            <Group mt="md" grow>
                <NumberInput label="Minimum" value={min} onChange={(v) => onChange({ min: typeof v === 'number' ? v : undefined })} />
                <NumberInput label="Maximum" value={max} onChange={(v) => onChange({ max: typeof v === 'number' ? v : undefined })} />
            </Group>
        );
    }

    if (outputType === 'currency') {
        const code = typeof outputConfig.currencyCode === 'string' ? outputConfig.currencyCode : 'USD';
        return (
            <Select
                mt="md"
                label="Currency"
                data={CURRENCY_OPTIONS.map((c) => ({ value: c, label: c }))}
                value={code}
                onChange={(v) => onChange({ currencyCode: v ?? 'USD' })}
                allowDeselect={false}
            />
        );
    }

    if (outputType === 'category') {
        const options = Array.isArray(outputConfig.options) ? (outputConfig.options as string[]) : [];
        return (
            <TagsInput
                mt="md"
                label="Categories"
                placeholder="Add an option and press Enter"
                value={options}
                onChange={(value) => onChange({ options: value })}
            />
        );
    }

    return null;
}
